import getPath from '../getPath/getPath';
import setPath from '../setPath/setPath';
import getCreditCardType from '../getCreditCardType/getCreditCardType';

interface GiftCard {
  BIN_NUMBER: number;
  MIN_RANGE: number;
  MAX_RANGE: number;
}

/**
 * Test the validity of an email address using the browser's built-in HTML5 form validation API.
 * @param  {String} value An email address.
 * @return {Boolean}
 */
const testEmail = (value: string): boolean => {
  const emailRe =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return emailRe.test(value);
};

/**
 * Test the validity of the name and make sure it does not use special characters
 * except for characters like ' for O'Niel
 * @param  {String} value A name.
 * @return {Boolean}
 */
const testName = (value: string): boolean => {
  const nameRe = /^[a-z ,.'-]+$/i;
  return nameRe.test(value);
};

/**
 * Test the validity of a phone number.
 * @param  {String} value A phone number.
 * @return {Booleanean}
 */
const testTel = (value: string): boolean => {
  const onlyNums = value.replace(/[^\d]/g, '');
  return onlyNums.length === 10;
};

/**
 * Test the validity of an address.
 * @param  {String} value an address.
 * @return {Boolean}
 */
const testAddress = (value: string): boolean => {
  const addressRegex = /^[A-Za-z0-9 .,-/'#]+$/g;
  return addressRegex.test(value);
};

/**
 * Format the appearance of a phone number in real-time via redux-form normalization.
 * @param  {String} value A phone number typed in by a human.
 * @return {String}       A formatted verion of the phone number. (123) 456-7890
 */
const phoneNormalization = (value: string | undefined | null): string => {
  if (value === undefined || value === null) return '';
  const nums = value.replace(/[^\d]/g, '');
  if (nums.length <= 2) return `${nums}`;
  if (nums.length <= 5) return `(${nums.slice(0, 3)}) ${nums.slice(3)}`;
  return `(${nums.slice(0, 3)}) ${nums.slice(3, 6)}-${nums.slice(6, 10)}`;
};

/**
 * Format the appearance of a credit card number.
 * @param  {String} value A credit card number typed in by a human.
 * @return {String}       A formatted verion of the credit card.
 */
const creditCardNormalization = (value: string | undefined | null): string => {
  if (value === undefined || value === null) return '';
  const nums = value.replace(/[^\d]/g, '');
  const cardType = getCreditCardType(nums).type;
  if (cardType === 'american-express') {
    if (nums.length <= 4) return `${nums}`;
    if (nums.length <= 10) return `${nums.slice(0, 4)}${nums.slice(4)}`;
    if (nums.length <= 15)
      return `${nums.slice(0, 4)}${nums.slice(4, 10)}${nums.slice(10, 15)}`;
    return `${nums.slice(0, 4)}${nums.slice(4, 10)}${nums.slice(10, 15)}`;
  }
  if (cardType === 'express' && nums.length === 9) {
    return nums.slice(0, 9);
  }
  if (nums.length <= 4) return `${nums}`;
  if (nums.length <= 8) return `${nums.slice(0, 4)}${nums.slice(4)}`;
  if (nums.length <= 12)
    return `${nums.slice(0, 4)}${nums.slice(4, 8)}${nums.slice(8, 12)}`;
  if (nums.length <= 16)
    return `${nums.slice(0, 4)}${nums.slice(4, 8)}${nums.slice(
      8,
      12
    )}${nums.slice(12, 16)}`;
  return `${nums.slice(0, 4)}${nums.slice(4, 8)}${nums.slice(
    8,
    12
  )}${nums.slice(12, 16)}`;
};

/**
 * Format the appearance of a home activation gift card number.
 * @param  {String} value A phone number typed in by a human.
 * @return {String}       A formatted verion of the gift card number.
 */
const haGiftCardNormalization = (value: string | undefined | null): string => {
  if (value === undefined || value === null) return '';
  const nums = value.replace(/[^\d]/g, '');
  return `${nums.slice(0, 19)}`;
};

/**
 * Format the appearance of a person id.
 * @param  {String} value An id typed in by a human.
 * @return {String}       A formatted verion of a person id.
 */
const ssnNormalization = (value: string | undefined | null): string => {
  if (value === undefined || value === null) return '';
  const nums = value.replace(/[^\d]/g, '');

  if (nums.length <= 3) return `${nums}`;
  if (nums.length <= 5) return `${nums.slice(0, 3)}-${nums.slice(3, 5)}`;
  return `${nums.slice(0, 3)}-${nums.slice(3, 5)}-${nums.slice(5, 9)}`;
};

/**
 * Test the validity of a required field.
 * @param  {String} key      A form field name attribute.
 * @param  {String} humanKey A human-friendly version of the field name attribute
 * @param  {Object} values   The values object returned by redux-form
 * @param  {Object} errors   The errors object supplied by redux-form
 * @return {Object}          An mutated version of the errors object with any new errors added.
 */
const testRequired = (
  key: string,
  humanKey: string,
  values: Record<string, string | number | undefined> | undefined,
  errors: Record<string, string | number | undefined>
): Record<string, string> | undefined => {
  // Why getPath?
  // It makes sense to use getPath here for nested values
  // for instance if we want to test keys such as billing.firstName.
  // for const test = {
  //  billing: {
  //   firstName: "Jane"
  //  }; getPath(test, 'billing.firstName', '') will return Jane while test?.[billing.firstName] will return undefined.
  // }
  // To Do: Refactor code so all scenarios are covered without getPath
  let testValue = getPath(values, key, '');
  // Make sure that when we test a string there are no blank spaces that can
  // create a false positive for a required field
  if (typeof testValue === 'string') {
    testValue = testValue.trim();
  }
  if (testValue === undefined || testValue === null || testValue === '') {
    return setPath(errors, key, `The ${humanKey} field is required.`);
  }
};

/**
 * Test address line1 of shipping and billing address
 * @param  {String} key      A form field name attribute.
 * @param  {String} humanKey A human-friendly version of the field name attribute
 * @param  {Object} values   The values object returned by redux-form
 * @param  {Object} errors   The errors object supplied by redux-form
 * @return {Object}          An mutated version of the errors object with any new errors added.
 */
const testAddressCharacters = (
  key: string,
  humanKey: string,
  values: Record<string, string | number | undefined> | undefined,
  errors: Record<string, string | number | undefined>
): Record<string, string> | undefined => {
  // Why getPath?
  // It makes sense to use getPath here for nested values
  // for instance if we want to test keys such as billing.firstName.
  // for const test = {
  //  billing: {
  //   firstName: "Jane"
  //  }; getPath(test, 'billing.firstName', '') will return Jane while test?.[billing.firstName] will return undefined.
  // }
  // To Do: Refactor code so all scenarios are covered without getPath
  let testValue: string | number | boolean | object | any[] | undefined =
    getPath(values, key, '');
  // Make sure that when we test a string there are no blank spaces that can
  // create a false positive for a required field
  if (typeof testValue === 'string') {
    testValue = testValue.trim();
  }
  if (testValue === undefined || testValue === null || testValue === '') {
    const error = setPath(errors, key, `The ${humanKey} field is required.`);
    return error;
  }

  if (typeof testValue === 'string') {
    const validateAddress = /^[a-zA-Z0-9. ,#-]+$/.test(testValue);
    if (!validateAddress) {
      const error = setPath(
        errors,
        key,
        `Check the ${humanKey} you have entered.`
      );
      return error;
    }
  }
};

/**
 * Perform validation checks on a potential new password.
 * @param  {String} value A password candidate
 * @return {array}  A list of error codes.  If the array length is 0, then there were no errors.
 */
const testSecurePassword = (value: string): string[] => {
  const errors = [];

  // must be at least 7 characters in length
  if (value.length < 7) errors.push('short');

  // cannot be longer than 20 characters
  if (value.length > 20) errors.push('long');

  // spaces not allowed
  if (/\s/.test(value)) errors.push('space');

  // needs at least one upper character
  if (!/[A-Z]/.test(value)) errors.push('upper');

  // needs at least one lower character
  if (!/[a-z]/.test(value)) errors.push('lower');

  // needs at least one numeric character
  if (!/[0-9]/.test(value)) errors.push('number');

  return errors;
};

/**
 * Test to see if two emails are the same ignoring case.
 * @param  {String} email        Email A
 * @param  {String} confirmEmail Email B
 * @return {Boolean}              True if they match (ignoring case), otherwise false.
 */
const testEmailsMatch = (email: string, confirmEmail: string): boolean =>
  email.toLowerCase() === confirmEmail.toLowerCase();

/**
 * Test the length of a credit card.
 * @param  {String} value  Credit Card number.
 * @return {Boolean}          True if the length matches the credit card type.
 */
const testCreditCardNumberLength = (value: string): boolean => {
  const onlyNums = value.replace(/[^\d]/g, '');
  const matchedCreditCardLength = getCreditCardType(onlyNums).lengths;
  if (onlyNums.length === 0) {
    return true;
  }
  return onlyNums.length === matchedCreditCardLength;
};

/**
 * Test to see if value only contains numbers.
 * @return {Boolean}              True if it is only numbers, otherwise false.
 */
const testNumbersOnly = (value: string): boolean => {
  const onlyNums = /^\d+$/.test(value);
  return onlyNums;
};

/**
 * Test last 4 of SSN.
 * @param  {String} value  Last 4 of SSN.
 * @return {Boolean}          True if it is the last 4 of SSN
 */
const testLast4SSN = (value: string): boolean => {
  return /^\d{4}$/.test(value);
};

/**
 * Test home activation gift card number
 * @param  {String} value               value of the gift card number
 * @param  {Object} HA_GIFT_CARD_RANGE  contains the range and bin number
 * @return {boolean}                    True when all tests pass
 */

const testHAGiftCardNumber = (
  value: string | number | undefined,
  HA_GIFT_CARD_RANGE: GiftCard
): boolean => {
  const { BIN_NUMBER, MIN_RANGE, MAX_RANGE } = HA_GIFT_CARD_RANGE;

  // test that the values are numbers only
  if (value !== undefined && !testNumbersOnly(value.toString())) {
    return false;
  }

  // ensure that the length of the gift card number is 19 digits
  if (
    value === undefined ||
    typeof value !== 'string' ||
    value.length !== `${BIN_NUMBER}${MAX_RANGE}`.length
  ) {
    return false;
  }

  // ensure that the gift card number is in the 600649610 bin
  const binNumber = value.slice(0, BIN_NUMBER.toString().length);
  if (binNumber !== BIN_NUMBER.toString()) {
    return false;
  }

  // ensure that the value after the bin is in range
  const giftCardNumber = Number(value.slice(BIN_NUMBER.toString().length));
  return giftCardNumber >= MIN_RANGE && giftCardNumber <= MAX_RANGE;
};

export default {
  phoneNormalization,
  creditCardNormalization,
  haGiftCardNormalization,
  ssnNormalization,
  testEmail,
  testName,
  testAddress,
  testRequired,
  testAddressCharacters,
  testTel,
  testSecurePassword,
  testEmailsMatch,
  testCreditCardNumberLength,
  testNumbersOnly,
  testLast4SSN,
  testHAGiftCardNumber,
};
